/**
 *  @class
 *  @function Pagination
 *
 *  Load More / infinite scroll. Each appended page rewrites the browser URL to the
 *  ?page=N it just fetched, so leaving the list and pressing back lands on a URL that
 *  says where the customer was — bh-list-restore.js reads it and rebuilds the list.
 */

if (!customElements.get('pagination-theme')) {
  class Pagination extends HTMLElement {
    constructor() {
      super();
      const id = this.dataset.section;
      this.sectionId = id;
      this.button = this.querySelector('.load-more');
      this.wrapper = this.closest('.pagination--wrapper');
      this.grid = document.querySelector(`[data-id="${id}"]`);
      // Landing on ?page=3 has to continue from 3. Starting at 1 fetched page 2
      // and appended it *below* page 3.
      this.page = parseInt(this.dataset.currentPage, 10) || 1;
      this.totalPages = parseInt(this.dataset.totalPages, 10) || Infinity;
    }

    connectedCallback() {
      if (this.classList.contains('pagination-type--loadmore')) {
        this.initLoadMore();
      }
      if (this.classList.contains('pagination-type--infinite')) {
        this.initInfiniteScroll();
      }
    }

    addUrlParam(search, key) {
      const newParam = `${key}=${this.page}`;
      if (!search) return `?${newParam}`;

      const params = search.replace(new RegExp(`([?&])${key}[^&]*`), `$1${newParam}`);
      return params === search ? `${search}&${newParam}` : params;
    }

    initLoadMore() {
      this.button.addEventListener('click', () => {
        this.loadProducts();
        this.button.blur();
      });
    }

    initInfiniteScroll() {
      this.observer = new IntersectionObserver(entries => {
        if (entries[0].intersectionRatio === 1) {
          this.loadProducts();
        }
      }, {
        threshold: [0, 1]
      });
      this.observer.observe(this);
    }

    async loadProducts() {
      if (this.hasAttribute('loading')) return;

      this.page++;
      this.setAttribute('loading', 'true');
      const url = `${document.location.pathname}${this.addUrlParam(document.location.search, 'page')}`;

      try {
        // Section-only render: the full document is ~285KB to extract one grid from.
        const response = await fetch(Pagination.sectionUrl(this.sectionId, url));
        const responseText = await response.text();
        this.renderProducts(responseText);
        this.renderCounts(responseText);

        // replaceState, not pushState: pushing one entry per click would make the back
        // button mean "undo a Load More" — five clicks, six presses to leave the list.
        // The { searchParams } shape is what facets.js's popstate handler reads.
        history.replaceState({ searchParams: url.slice(url.indexOf('?') + 1) }, '', url);

        dispatchCustomEvent('pagination:page-changed', { url });
      } catch (error) {
        console.error('Error loading products:', error);
      } finally {
        this.removeAttribute('loading');
      }
    }

    static sectionUrl(sectionId, url) {
      const [path, search] = url.split('?');
      return `${path}?section_id=${sectionId}${search ? `&${search}` : ''}`;
    }

    renderProducts(html) {
      const gridToReplace = new DOMParser().parseFromString(html, 'text/html').getElementById('product-grid');
      if (!gridToReplace) {
        this.hide();
        return;
      }

      // A promo card pinned last has to stay last, so loaded products go in above it.
      // insertBefore(node, null) is appendChild, so no anchor is the no-promo case.
      const pinnedLast = this.grid.querySelector('.promotion-column--last');

      // Liquid drops a promo card at a fixed position within every page, so appending
      // page 2+ verbatim repeats the same card down the list. Page 1's is the real one.
      gridToReplace.querySelectorAll('.columns:not(.promotion-column)').forEach((product) => {
        this.grid.insertBefore(product, pinnedLast);
        product.querySelectorAll('.lazyload').forEach((image) => {
          lazySizes.loader.unveil(image);
        });
      });

      if (this.page >= this.totalPages) {
        this.hide();
      }
    }

    renderCounts(html) {
      const newCounts = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount');

      if (!newCounts) {
        return;
      }
      this.wrapper.querySelector('#ProductCount').innerHTML = newCounts.innerHTML;
    }

    hide() {
      if (this.observer) this.observer.unobserve(this);
      if (this.button) this.button.style.display = 'none';
    }
  }

  customElements.define('pagination-theme', Pagination);
}
